import React, { useCallback, useEffect, useState } from 'react';
import applicantService from '../services/applicantService';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import FormField from '../components/common/FormField';
import { formatDateTime } from '../utils/formatters';
import './ApplicantPages.css';

const CHECK_LABELS = {
  identity: 'Identity check',
  income: 'Income check',
  credit: 'Credit check',
  employment_reference: 'Employer reference',
  landlord_reference: 'Previous landlord reference',
  reuse: 'Reuse of my verified profile'
};

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

// The applicant's rights in one place: see what they agreed to, withdraw it, get a copy, ask for deletion.
const MyDataPage = () => {
  const [consents, setConsents] = useState(null);
  const [applications, setApplications] = useState([]);
  const [notice, setNotice] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [history, apps] = await Promise.all([applicantService.consentHistory(), applicantService.myApplications()]);
    setConsents(history);
    setApplications(apps);
  }, []);

  useEffect(() => {
    load().catch((err) => setNotice({ type: 'error', message: messageFrom(err, 'Your data could not be loaded.') }));
  }, [load]);

  const withdraw = async () => {
    setBusy(true);
    try {
      await applicantService.withdrawConsent(withdrawing.id, reason);
      setWithdrawing(null);
      setReason('');
      await load();
      setNotice({ type: 'success', message: 'Consent withdrawn. No further data will be collected for that check.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Consent could not be withdrawn.') });
    } finally {
      setBusy(false);
    }
  };

  const requestDeletion = async () => {
    setBusy(true);
    try {
      await applicantService.requestDeletion(deleting.id, deleteReason);
      setDeleting(null);
      setDeleteReason('');
      setNotice({ type: 'success', message: 'Your deletion request has been received. It will be reviewed and you will be told the outcome.' });
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Your request could not be sent.') });
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const blob = await applicantService.exportData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'my-tenantverify-data.json';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Your data could not be exported.') });
    }
  };

  if (!consents) return notice ? <Alert type={notice.type} message={notice.message} /> : <LoadingSpinner />;

  return (
    <div className="ap-page">
      <h1>My data</h1>
      <p className="ap-muted">
        You are in control of your information. You can see what you agreed to, withdraw consent, download a copy, or ask
        for your data to be deleted.
      </p>

      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      <section className="ap-card" aria-labelledby="consent-history-title">
        <h2 id="consent-history-title">What you agreed to</h2>
        {consents.length === 0 ? (
          <p>You have not given or refused any consent yet.</p>
        ) : (
          <ul className="ap-rows">
            {consents.map((c) => (
              <li key={c.id}>
                <div>
                  <strong>{CHECK_LABELS[c.check_type] || c.check_type}</strong>{' '}
                  <span className={`ap-pill ap-pill--${c.superseded ? 'withdrawn' : c.choice}`}>
                    {c.superseded ? 'granted, later withdrawn' : c.choice}
                  </span>
                  <div className="ap-muted">{c.property || 'Application'} · {formatDateTime(c.created_at)}</div>
                  {c.purpose && (
                    <div className="ap-muted">Purpose: {c.purpose}. Shared with: {c.recipients}. {c.retention_info}.</div>
                  )}
                </div>
                {c.can_withdraw && (
                  <button type="button" className="ap-button ap-button--ghost" onClick={() => setWithdrawing(c)}>
                    Withdraw
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="ap-card" aria-labelledby="copy-title">
        <h2 id="copy-title">A copy of your data</h2>
        <p>Download everything we hold about you, in a file you can read or keep.</p>
        <button type="button" className="btn-primary ap-button" onClick={download}>Download my data</button>
      </section>

      <section className="ap-card" aria-labelledby="delete-title">
        <h2 id="delete-title">Ask for your data to be deleted</h2>
        {applications.length === 0 ? (
          <p>You have no applications.</p>
        ) : (
          <ul className="ap-rows">
            {applications.map((a) => (
              <li key={a.id}>
                <div><strong>{a.property?.address || `Application ${a.id}`}</strong></div>
                <button type="button" className="ap-button ap-button--ghost" onClick={() => setDeleting(a)}>
                  Request deletion
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal isOpen={!!withdrawing} onClose={() => setWithdrawing(null)} title="Withdraw consent">
        <p>
          Withdraw your consent for the{' '}
          <strong>{withdrawing && (CHECK_LABELS[withdrawing.check_type] || withdrawing.check_type)}</strong>? The
          landlord may not be able to finish assessing your application.
        </p>
        <FormField label="Reason" name="reason" type="textarea" value={reason} onChange={(e) => setReason(e.target.value)} required />
        <button type="button" className="btn-primary ap-button" disabled={busy || !reason} onClick={withdraw}>
          {busy ? 'Withdrawing...' : 'Withdraw consent'}
        </button>
      </Modal>

      <Modal isOpen={!!deleting} onClose={() => setDeleting(null)} title="Request deletion">
        <p>
          Ask for the data for <strong>{deleting?.property?.address || 'this application'}</strong> to be deleted. Some
          records may have to be kept for the period required by law, and you will be told the outcome.
        </p>
        <FormField label="Reason" name="deleteReason" type="textarea" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} required />
        <button type="button" className="btn-primary ap-button" disabled={busy || !deleteReason} onClick={requestDeletion}>
          {busy ? 'Sending...' : 'Send request'}
        </button>
      </Modal>
    </div>
  );
};

export default MyDataPage;
